import { Dialog } from 'components/dialogs/Dialog'
import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useMemo, useRef, useState, useId } from 'react'
import { PurchaseDialogBody } from './PurchaseDialogBody'
import { EnrollDialogBody } from './EnrollDialogBody'
import type { StudentAssignmentCubit } from '@breakoutlearning/firebase-repository/cubits/StudentAssignmentCubit'
import { useBreakoutUser } from 'hooks/profile'
import { DialogCloseButton } from 'components/dialogs/DialogCloseButton'
import { useRootStore } from 'hooks/rootStore'
import { Spinner } from 'components/Spinner'
import { BreakoutTextInput } from 'components/design-system/BreakoutTextInput'
import { BreakoutButton } from 'components/design-system/BreakoutButton'
import { useDialogs } from 'hooks/dialogs'
import { toast } from 'react-hot-toast'
import classNames from 'classnames'
import { BreakoutUserAvatar } from 'components/breakout/BreakoutUserAvatar'
import { type SlideDeck } from '@breakoutlearning/firebase-repository/models/SlideDeck'
import {
  BreakoutRadioButton,
  BreakoutRadioGroup,
} from 'components/design-system/BreakoutRadioButton'
import { TicketIcon } from 'components/icons/Ticket'
import { useRepository } from 'hooks/auth'
import { useTranslationTyped } from 'i18n/i18n'
import { DialogTopSectionWithIcon } from 'components/dialogs/DialogTopSectionWithIcon'
import { StripeCheckoutBlockedView } from './StripeCheckoutBlockedView'
import { useStripeCheckout } from './useStripeCheckout'
import { AccessCodeErrorCallout } from './AccessCodeErrorCallout'
import { type AccessCodeError, accessCodeError } from './accessCodeError'

type TypedTranslations = ReturnType<typeof useTranslationTyped>['tt']

function accessCodeAccepted(
  tt: TypedTranslations,
  cubit: StudentAssignmentCubit
): string {
  const { className, sectionName } = cubit.section.data
  const section = [className, sectionName].filter(Boolean).join(' — ')
  return section
    ? tt.pages.student.assignment.dialogs.purchase_or_enroll.access_code_accepted(
        { section }
      )
    : tt.student_assignment.coupon_redeemed()
}

function ProcessingView({ headingId }: { headingId: string }) {
  const { tt } = useTranslationTyped()
  return (
    <>
      <div className="flex h-full flex-1 flex-col items-center justify-center gap-5 text-center">
        <h1 id={headingId} className="text-title-large">
          {tt.student_assignment.processing_payment()}
        </h1>
        <Spinner />
      </div>
    </>
  )
}

export const PurchaseOrEnrollDialog = observer(function PurchaseOrEnrollDialog({
  cubit,
  closeSelf,
}: {
  cubit: StudentAssignmentCubit
  closeSelf: () => void
}) {
  const repo = useRepository()
  const rootStore = useRootStore()
  const user = useBreakoutUser()
  const [showProcessing, setShowProcessing] = useState(false)

  useEffect(() => {
    cubit.onDialogMountUnmount({
      event: 'mount',
      dialogType: 'enroll',
      closeSelf,
    })
    return () => {
      cubit.onDialogMountUnmount({ event: 'unmount', dialogType: 'enroll' })
    }
  }, [closeSelf, cubit])

  const queryParams = rootStore.router.queryParams
  const isPostSuccess = queryParams?.payment === 'success'

  const slideDeckPrice = cubit.slideDeck.data.slideDeckPrice
  const tokensRequired = slideDeckPrice - user.availableTokens

  const DialogContents = useMemo(() => {
    if (showProcessing) return ProcessingView
    if (tokensRequired > 0 || cubit.section.usesSectionPass)
      return PurchaseDialogBody
    return EnrollDialogBody
  }, [cubit.section.usesSectionPass, showProcessing, tokensRequired])

  useEffect(() => {
    let timeout: NodeJS.Timeout | undefined
    // when we have a successful payment, but the tokens are not yet updated
    // show a processing view, but time it out after 10 seconds
    if (isPostSuccess && tokensRequired > 0) {
      repo.logEvent('post_success_processing_started')
      setShowProcessing(true)
      timeout = setTimeout(() => {
        repo.logEvent('post_success_processing_timed_out')
        setShowProcessing(false)
      }, 10000)
    } else {
      setShowProcessing(false)
    }

    return () => {
      if (timeout) clearTimeout(timeout)
    }
  }, [isPostSuccess, tokensRequired, repo])

  useEffect(() => {
    repo.logEvent('purchase_or_enroll_dialog_opened')
  }, [repo])

  const headingId = useId()

  return (
    <Dialog
      size="md"
      testId="enrollment-dialog"
      innerClassName="flex flex-col items-center justify-between gap-2"
      ignoreBackdropClick
      aria-labelledby={headingId}
    >
      <DialogCloseButton />
      <DialogContents cubit={cubit} headingId={headingId} />
    </Dialog>
  )
})

export const RedeemCouponDialog = observer(function RedeemCouponDialog({
  cubit,
  closeSelf,
}: {
  cubit: StudentAssignmentCubit
  closeSelf: () => void
}) {
  const repo = useRepository()
  const [formError, setFormError] = useState<AccessCodeError>()
  const [isRedeeming, setIsRedeeming] = useState(false)
  const { popDialog } = useDialogs()
  const { tt } = useTranslationTyped()
  const headingId = useId()
  const errorId = useId()

  useEffect(() => {
    cubit.onDialogMountUnmount({
      event: 'mount',
      dialogType: 'enroll',
      closeSelf,
    })
    return () => {
      cubit.onDialogMountUnmount({ event: 'unmount', dialogType: 'enroll' })
    }
  }, [closeSelf, cubit])

  const redeemCoupon = useCallback(
    async (couponCode: string) => {
      setFormError(undefined)
      if (!couponCode)
        return setFormError({
          title:
            tt.pages.student.assignment.dialogs.purchase_or_enroll.coupon_code_required(),
        })
      setIsRedeeming(true)
      let redeemed = false
      try {
        const result = await cubit.redeemSectionPassCoupon(couponCode)
        redeemed = result.success
        if (!result.success)
          setFormError(accessCodeError(tt, result.errorCode, result.details))
      } catch (e) {
        setFormError(accessCodeError(tt, 'UNKNOWN'))
      } finally {
        setIsRedeeming(false)
        if (redeemed) {
          toast.success(accessCodeAccepted(tt, cubit))
          popDialog()
        }
      }
    },
    [cubit, popDialog, tt]
  )

  useEffect(() => {
    repo.logEvent('redeem_coupon_dialog_opened')
  }, [repo])

  const inputRef = useRef<HTMLInputElement>(null)

  return (
    <Dialog
      size="md"
      innerClassName="flex flex-col items-center justify-between gap-2"
      ignoreBackdropClick
      aria-labelledby={headingId}
    >
      <DialogCloseButton />
      <h1 id={headingId} className="mb-4px text-center text-headline-large">
        {tt.student_assignment.redeem_coupon_code()}
      </h1>
      <div className="flex flex-1 items-center justify-center">
        <p className="text-center text-body-medium">
          {tt.student_assignment.redeeming_coupon_code_description()}
        </p>
      </div>
      <div className="flex w-full flex-1 flex-col justify-end gap-2">
        <BreakoutTextInput
          kind="secondary"
          ref={inputRef}
          disabled={isRedeeming}
          placeholder={tt.student_assignment.enter_coupon_code()}
          showErrorOutline={!!formError}
          aria-invalid={!!formError}
          aria-describedby={formError ? errorId : undefined}
          onChange={() => setFormError(undefined)}
        />
        {formError && <AccessCodeErrorCallout id={errorId} error={formError} />}
        <BreakoutButton
          fullWidth
          size="large"
          loading={isRedeeming}
          disabled={isRedeeming}
          onClick={() => {
            repo.logEvent('redeem_coupon_button_clicked')
            redeemCoupon(inputRef.current?.value || '')
          }}
        >
          {tt.student_assignment.apply()}
        </BreakoutButton>
      </div>
    </Dialog>
  )
})

export const SelectCouponOrStripeDialog = observer(
  function SelectCouponOrStripeDialog({
    cubit,
    showStripeDialog,
    showCouponDialog,
    closeSelf,
  }: {
    cubit: StudentAssignmentCubit
    showStripeDialog: VoidFunction
    showCouponDialog: VoidFunction
    closeSelf: () => void
  }) {
    const { popDialog } = useDialogs()
    const { tt } = useTranslationTyped()
    const headingId = useId()

    useEffect(() => {
      cubit.onDialogMountUnmount({
        event: 'mount',
        dialogType: 'enroll',
        closeSelf,
      })
      return () => {
        cubit.onDialogMountUnmount({ event: 'unmount', dialogType: 'enroll' })
      }
    }, [closeSelf, cubit])

    return (
      <Dialog
        size="sm"
        innerClassName="flex flex-col items-center justify-evenly gap-2"
        ignoreBackdropClick
        aria-labelledby={headingId}
      >
        <DialogCloseButton />
        <h1 id={headingId} className="mb-4px text-center text-headline-large">
          {tt.pages.student.assignment.dialogs.purchase_or_enroll.select_purchase_method()}
        </h1>
        <div className="flex flex-col gap-3">
          <BreakoutButton
            fullWidth
            size="medium"
            onClick={() => {
              popDialog()
              setTimeout(showStripeDialog, 0)
            }}
          >
            {tt.student_assignment.pay_using_stripe()}
          </BreakoutButton>
          <BreakoutButton
            fullWidth
            size="medium"
            onClick={() => {
              popDialog()
              setTimeout(showCouponDialog, 0)
            }}
          >
            {tt.student_assignment.redeem_coupon_code()}
          </BreakoutButton>
        </div>
      </Dialog>
    )
  }
)

export const EnrollWithSectionPassDialog = observer(
  function EnrollWithSectionPassDialog({
    cubit,
    usesStripePurchase,
    usesCouponCodes,
    closeSelf,
  }: {
    cubit: StudentAssignmentCubit
    usesStripePurchase: boolean
    usesCouponCodes: boolean
    closeSelf: () => void
  }) {
    const repo = useRepository()
    const [paymentMethod, setPaymentMethod] = useState<'stripe' | 'coupon'>(
      usesStripePurchase ? 'stripe' : 'coupon'
    )
    const [couponCode, setCouponCode] = useState('')
    const [formError, setFormError] = useState<AccessCodeError>()
    const [isRedeeming, setIsRedeeming] = useState(false)
    const { popDialog } = useDialogs()
    const { tt } = useTranslationTyped()
    const { startCheckout, waitingForStripeURL, blockedCheckoutUrl } =
      useStripeCheckout(cubit)
    const headingId = useId()
    const errorId = useId()

    useEffect(() => {
      cubit.onDialogMountUnmount({
        event: 'mount',
        dialogType: 'enroll',
        closeSelf,
      })
      return () => {
        cubit.onDialogMountUnmount({ event: 'unmount', dialogType: 'enroll' })
      }
    }, [closeSelf, cubit])

    const redeemCoupon = useCallback(
      async (code: string) => {
        setFormError(undefined)
        if (!code)
          return setFormError({
            title:
              tt.pages.student.assignment.dialogs.purchase_or_enroll.coupon_code_required(),
          })
        setIsRedeeming(true)
        repo.logEvent('redeem_coupon_button_clicked')
        try {
          const result = await cubit.redeemSectionPassCoupon(code)
          if (!result.success)
            setFormError(accessCodeError(tt, result.errorCode, result.details))
          if (result.success) {
            toast.success(accessCodeAccepted(tt, cubit))
            popDialog()
          }
        } catch (e) {
          repo.logEvent('redeem_coupon_button_failed')
          setFormError(accessCodeError(tt, 'UNKNOWN'))
        } finally {
          setIsRedeeming(false)
        }
      },
      [cubit, popDialog, repo, tt]
    )

    useEffect(() => {
      repo.logEvent('enroll_with_section_pass_dialog_opened')
    }, [repo])

    const showRadioToggle = usesStripePurchase && usesCouponCodes

    const slideDecks = useMemo(() => {
      // Dedupe slide decks, incase there are multiple assignments with the same slide deck
      const slideDecksMap = Object.fromEntries(
        cubit.sectionAssignmentsWithSlideDecks
          .filter(({ slideDeck }) => slideDeck)
          .map(({ slideDeck }) => [slideDeck!.id, slideDeck as SlideDeck])
      )
      return Object.values(slideDecksMap)
    }, [cubit.sectionAssignmentsWithSlideDecks])

    if (blockedCheckoutUrl) {
      return (
        <Dialog
          testId="enrollment-section-pass-dialog"
          size="lg"
          className="flex flex-col items-center"
          innerClassName="flex flex-col h-full min-h-[650px] max-w-[554px]"
          ignoreBackdropClick
          aria-labelledby={headingId}
        >
          <DialogCloseButton />
          <StripeCheckoutBlockedView
            headingId={headingId}
            checkoutUrl={blockedCheckoutUrl}
          />
        </Dialog>
      )
    }

    return (
      <Dialog
        testId="enrollment-section-pass-dialog"
        size="lg"
        className="flex flex-col items-center"
        innerClassName="flex flex-col h-full min-h-[650px] max-w-[554px]"
        ignoreBackdropClick
        aria-labelledby={headingId}
      >
        <DialogCloseButton />
        <DialogTopSectionWithIcon
          ColoredIcon={
            <TicketIcon size={44} className="stroke-breakout-yellow" />
          }
          title={tt.student_assignment.enroll_with_section_pass()}
          description={tt.student_assignment.enroll_with_section_pass_description()}
          titleId={headingId}
        />

        <div className="flex-1">
          <div className="mt-4 flex flex-col gap-4">
            <div className="flex items-center justify-between rounded-2xl bg-core-secondary px-4 py-3">
              <div className="flex flex-col">
                <div className="text-title-small">
                  {cubit.section.data.className}
                </div>
                <div className="text-body-medium text-on-surface-var">
                  {cubit.section.data.sectionName}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <BreakoutUserAvatar
                  user={cubit.section.instructor}
                  radius={16}
                />
                <div className="text-title-small">
                  {cubit.section.instructor.fullName}
                </div>
              </div>
            </div>
            <div className="max-h-[200px] overflow-y-auto">
              {slideDecks.map((slideDeck) => (
                <DisplayedSlideDeck key={slideDeck.id} slideDeck={slideDeck} />
              ))}
            </div>
          </div>
        </div>

        {showRadioToggle && (
          <div className="mt-6">
            <div className="mb-2 text-label-large">
              {tt.student_assignment.select_a_payment_method()}
            </div>
            <BreakoutRadioGroup
              value={paymentMethod}
              onValueChange={(val) =>
                setPaymentMethod(val as 'stripe' | 'coupon')
              }
              className="flex gap-2"
              aria-label={tt.pages.student.assignment.dialogs.purchase_or_enroll.payment_method_selection()}
            >
              <BreakoutRadioButton
                data-testid="payment-method-stripe"
                label={tt.student_assignment.pay_using_stripe()}
                value="stripe"
              />
              <BreakoutRadioButton
                data-testid="payment-method-coupon"
                label={tt.student_assignment.access_code()}
                value="coupon"
              />
            </BreakoutRadioGroup>
          </div>
        )}

        <div
          className={classNames(
            'mt-6 flex gap-4',
            paymentMethod === 'coupon' ? 'justify-between' : 'justify-center'
          )}
        >
          {paymentMethod === 'coupon' && (
            <div className="flex flex-1 flex-col">
              <BreakoutTextInput
                testId="access-code-input"
                kind="secondary"
                disabled={isRedeeming}
                placeholder={tt.student_assignment.enter_access_code()}
                value={couponCode}
                onChange={(e) => {
                  setCouponCode(e.target.value)
                  setFormError(undefined)
                }}
                showErrorOutline={!!formError}
                aria-invalid={!!formError}
                aria-describedby={formError ? errorId : undefined}
              />
            </div>
          )}

          <PaymentButtonWithRebateInfo
            cubit={cubit}
            couponCode={couponCode}
            paymentMethod={paymentMethod}
            isRedeeming={isRedeeming}
            waitingForStripeURL={waitingForStripeURL}
            redeemCoupon={redeemCoupon}
            startCheckout={startCheckout}
          />
        </div>

        {/* under the whole row, so it spans the input and the button */}
        {paymentMethod === 'coupon' && formError && (
          <div className="mt-3">
            <AccessCodeErrorCallout id={errorId} error={formError} />
          </div>
        )}
      </Dialog>
    )
  }
)

const PaymentButtonWithRebateInfo = observer(
  function PaymentButtonWithRebateInfo({
    cubit,
    couponCode,
    paymentMethod,
    isRedeeming,
    waitingForStripeURL,
    redeemCoupon,
    startCheckout,
  }: {
    cubit: StudentAssignmentCubit
    couponCode: string
    paymentMethod: 'stripe' | 'coupon'
    isRedeeming: boolean
    waitingForStripeURL: boolean
    redeemCoupon: (couponCode: string) => Promise<void>
    startCheckout: ReturnType<typeof useStripeCheckout>['startCheckout']
  }) {
    const repo = useRepository()
    const { tt } = useTranslationTyped()

    const sectionStartDate = cubit.section.data.sectionStartDate
    const isSectionNotStarted =
      sectionStartDate && sectionStartDate > new Date()

    if (cubit.assignmentPurchasePrice.unitType !== 'cent')
      throw new Error(
        'PaymentButtonWithRebateInfo only supports cent-based pricing for section passes'
      )

    const {
      amount,
      amountStr: dollars,
      rebateAmountToApplyDollarStr,
      totalRebateAmountDollarStr,
    } = cubit.assignmentPurchasePrice

    const price = useMemo(() => {
      if (amount === 0) return tt.student_assignment.free()
      return tt.student_library.dollars({ dollars })
    }, [amount, dollars, tt])

    const paymentButton = (
      <BreakoutButton
        data-testid="pay-with-stripe-button"
        size="large"
        kind="accent"
        disabled={isSectionNotStarted}
        loading={isRedeeming || waitingForStripeURL}
        onClick={async () => {
          if (paymentMethod === 'coupon') {
            repo.logEvent('redeem_coupon_button_clicked')
            await redeemCoupon(couponCode)
          } else {
            await startCheckout(
              {
                type: 'sectionPass',
                sectionPassSectionId: cubit.section.id,
              },
              { clicked: 'purchase_tokens_button_clicked' }
            )
          }
        }}
      >
        {paymentMethod === 'coupon'
          ? tt.student_assignment.continue()
          : tt.student_assignment.enroll_price({ price })}
      </BreakoutButton>
    )

    if (paymentMethod === 'coupon' || rebateAmountToApplyDollarStr === null)
      return paymentButton // no rebate wrapper to display

    return (
      <div className="md: flex flex-1 flex-row flex-wrap items-center justify-center gap-5 rounded-2xl border border-outline-light p-5 sm:flex-nowrap">
        <div className="flex flex-col">
          <h4 className="text-title-medium">
            {tt.student_assignment.available_credit()}
          </h4>
          <strong className="text-body-medium text-on-surface-var">
            {tt.student_assignment.credit_to_apply({
              creditToApply: tt.student_library.dollars({
                dollars: rebateAmountToApplyDollarStr,
              }),
              creditAmount: tt.student_library.dollars({
                dollars: totalRebateAmountDollarStr,
              }),
            })}
          </strong>
        </div>
        {paymentButton}
      </div>
    )
  }
)

const DisplayedSlideDeck = observer(function DisplayedSlideDeck({
  slideDeck,
}: {
  slideDeck: SlideDeck
}) {
  return (
    <div className="mx-5 border-b border-border-grey py-3">
      <div className="flex items-start gap-3">
        <div className="bg-surface-container-high mt-0.5 flex h-6 w-6 items-center justify-center rounded-full">
          {slideDeck?.data.slideDeckImageURL && (
            <img
              src={slideDeck.data.slideDeckImageURL}
              alt=""
              className="max-h-[50px] w-auto"
            />
          )}
        </div>
        <div className="flex flex-1 flex-col">
          <div className="text-label-medium">
            {slideDeck?.data.slideDeckName}
          </div>
          <div className="text-body-medium text-on-surface-var">
            {slideDeck?.data.slideDeckName}
          </div>
        </div>
      </div>
    </div>
  )
})
